import { useNavigate } from 'react-router-dom';

import { useSession } from '@/hooks/useSession';
import { useAnalytics } from '@/hooks/useAnalytics';
import { PrivacyPolicy } from '@/modals/PrivacyPolicy';
import { DataConsent } from '@/modals/DataConsent';
import { MailingPolicy } from '@/modals/MailingPolicy';
import { ResumeSession } from '@/modals/ResumeSession';
import { AlreadyTested } from '@/modals/AlreadyTested';
import { getMaxBridge } from '@/lib/max-bridge';

/**
 * Точка монтирования всех модалок. Открытая модалка определяется состоянием
 * activeModal в SessionContext. Это даёт возможность открыть любую модалку
 * из любого экрана (см. UX-спека: модалки не через роуты).
 *
 * Поведение M4 (UX-спека 3.3):
 *  - «Продолжить» — оставляем восстановленную сессию, просто закрываем модалку.
 *  - «Начать заново» — RESET (стираем всё, кроме города), закрываем модалку.
 *
 * TODO Этап работ 4-6: подставить в текст M4 имя ребёнка и {N}/{M} прогресса
 * по UX-спеке 3.3.
 */
export function ModalHub() {
  const navigate = useNavigate();
  const { activeModal, closeModal, reset, resetForOtherChild, resetAnswers } =
    useSession();
  const { track } = useAnalytics();

  const handleResume = () => {
    // Сессия уже восстановлена в state — нет дополнительных действий, просто закрываем.
    closeModal();
  };

  const handleRestart = () => {
    // M4 «Начать заново» — полный RESET (всё кроме city).
    // saveSession в SessionProvider автоматически перезапишет storage свежим состоянием
    // на следующем рендере, поэтому отдельный clearSession() не нужен.
    reset();
    closeModal();
  };

  // M5 actions (Phase 3 stage 3 — изолированные reset-семантики).
  const handleM5OpenResult = () => {
    closeModal();
    navigate('/result');
  };

  // «Пройти для другого ребёнка»: тот же родитель → новый ребёнок. Сохраняет
  // contacts + consents + city, стирает child + ответы. Переход на ChildInfo
  // — public route, name-input будет пустым (draftName=null).
  const handleM5OtherChild = () => {
    track('m5_other_child_clicked');
    resetForOtherChild();
    closeModal();
    navigate('/child-info');
  };

  // «Пройти заново»: тот же ребёнок → новая попытка. Сохраняет child + ageGroup
  // + родительский контекст, стирает только ответы. Переход на /instruction
  // (guardInstruction проходит, т.к. child + ageGroup сохранены). При повторном
  // submit ContactsForm — pre-fill name работает через state.contacts?.name.
  const handleM5Restart = () => {
    track('m5_restart_clicked');
    resetAnswers();
    closeModal();
    navigate('/instruction');
  };

  const handleM5Close = () => {
    closeModal();
    try {
      getMaxBridge().close();
    } catch {
      // bridge может быть недоступен в тестах
    }
  };

  return (
    <>
      <PrivacyPolicy isOpen={activeModal === 'privacy-policy'} onClose={closeModal} />
      <DataConsent isOpen={activeModal === 'data-consent'} onClose={closeModal} />
      <MailingPolicy isOpen={activeModal === 'mailing-policy'} onClose={closeModal} />
      <ResumeSession
        isOpen={activeModal === 'resume-session'}
        onClose={closeModal}
        onResume={handleResume}
        onRestart={handleRestart}
      />
      <AlreadyTested
        isOpen={activeModal === 'already-tested'}
        onClose={closeModal}
        onOpenResult={handleM5OpenResult}
        onOtherChild={handleM5OtherChild}
        onRestart={handleM5Restart}
        onCloseApp={handleM5Close}
      />
    </>
  );
}
